import {
	faInfoCircle,
	faPlus,
	faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from '@tanstack/react-router';
import {
	type OrganizationUpdateDto,
	PhoneType,
	useFetchOrganizationByIdSuspense,
	useUpdateOrganization,
} from '@uturn/api/v1';
import { Permission, useAbac } from '@uturn/portal/abac';
import { MetaDataContext } from '@uturn/portal/context';
import { useCountryOptions } from '@uturn/portal/hooks/use-country-options';
import { shouldUpdate } from '@uturn/portal/modules/shipments/utils/form';
import { queryClient } from '@uturn/portal/query';
import { phoneNumber } from '@uturn/portal/zod/schema';
import {
	Button,
	Combobox,
	Form,
	FormControl,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Separator,
	Stars,
	sonner,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import { useCallback, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	BE_EMAILS_LIST_SEPARATOR,
	EMAIL_MIN_LENGTH,
	EmailsFieldArray,
	formProfileSchema,
	MAX_JOINED_LENGTH,
	maxCount,
	PhonesFieldArray,
	requiredFields,
	type TForm,
} from '../schema';

export function OrganizationProfile() {
	const { t } = useTranslation();
	const { userHasPermissions } = useAbac();
	const { metadata } = useContext(MetaDataContext);
	const { orgId } = useParams({
		from: '/_protected/org/$orgId/settings/',
	});
	const canUpdate = userHasPermissions(Permission.UPDATE_ORGANIZATION_PROFILE);

	const orgType = metadata?.user?.organisation?.organisationType ?? '';

	const { data: organization, queryKey } = useFetchOrganizationByIdSuspense(
		orgId,
		{
			query: {
				select: (data) => data.data,
			},
		},
	);

	const { mutate, isPending } = useUpdateOrganization();

	const onSubmit = (formData: TForm) => {
		const { phonesPrimary, phonesSecondary, ...restData } = formData;

		const data = {
			...restData,
			emails: restData.emails
				.map((email) => email.email)
				.filter((email) => email !== ''),
			phoneNumbers: [
				{
					...restData.phoneNumbers?.find((p) => p.type === PhoneType.PRIMARY),
					type: PhoneType.PRIMARY,
					phone: phoneNumber.join(
						phonesPrimary
							.map((phone) => phone.phone)
							.filter(phoneNumber.isType),
					),
				},
				{
					...restData.phoneNumbers?.find(
						(p) => p.type === PhoneType.OUT_OF_OFFICE,
					),
					type: PhoneType.OUT_OF_OFFICE,
					phone: phoneNumber.join(
						phonesSecondary
							.map((phone) => phone.phone)
							.filter(phoneNumber.isType),
					),
				},
			],
		} satisfies OrganizationUpdateDto;

		mutate(
			{ id: orgId, data },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey });

					// Remove empty email/phone number fields after successful submit
					form.setValue(
						'emails',
						form
							.getValues('emails')
							.filter((email, index) => index === 0 || email.email !== ''),
					);
					form.setValue(
						'phonesPrimary',
						form
							.getValues('phonesPrimary')
							.filter((phone, index) => index === 0 || phone.phone !== ''),
					);
					form.setValue(
						'phonesSecondary',
						form
							.getValues('phonesSecondary')
							.filter((phone, index) => index === 0 || phone.phone !== ''),
					);

					sonner.success(
						t(
							'pages.account.organisation.profile.form.submit.success.description',
							'Organisation has been updated',
						),
					);
				},
				onError: () => {
					sonner.error(t('general.try-again-later', 'Please try again later.'));
				},
			},
		);
	};

	const getEmailListOrEmptyString = useCallback((): EmailsFieldArray => {
		const result = organization.emails
			.filter((email) => email)
			.map((email) => ({ email }));
		if (result.length === 0) {
			return [{ email: '' }];
		}
		return result;
	}, [organization.emails]);

	const getPhoneListOrEmptyString = useCallback(
		(type: PhoneType): PhonesFieldArray => {
			const result = phoneNumber
				.split(
					organization.phoneNumbers?.find((phone) => phone.type === type)
						?.phone,
				)
				.filter(phoneNumber.isType)
				.map((phone) => ({ phone }));
			if (result.length === 0) {
				return [{ phone: '' }];
			}
			return result;
		},
		[organization.phoneNumbers],
	);

	const form = useForm<TForm>({
		resolver: zodResolver(formProfileSchema),
		mode: 'onChange',
		defaultValues: {
			...organization,
			emails: getEmailListOrEmptyString(),
			phonesPrimary: getPhoneListOrEmptyString(PhoneType.PRIMARY),
			phonesSecondary: getPhoneListOrEmptyString(PhoneType.OUT_OF_OFFICE),
		},
	});

	const isUpdating = form.formState.isSubmitting || isPending;

	// FieldArray for adding multiple emails/phone numbers,
	// while keeping zod validation for each single element.
	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'emails',
	});

	const phonesPrimaryFieldArray = useFieldArray({
		control: form.control,
		name: 'phonesPrimary',
	});

	const phonesSecondaryFieldArray = useFieldArray({
		control: form.control,
		name: 'phonesSecondary',
	});

	// Watch email/phone number arrays.
	const emailsWatch = form.watch('emails');
	const phonesPrimaryWatch = form.watch('phonesPrimary');
	const phonesSecondaryWatch = form.watch('phonesSecondary');

	const emailsCount = emailsWatch.length;
	const phonesPrimaryCount = phonesPrimaryWatch.length;
	const phonesSecondaryCount = phonesSecondaryWatch.length;

	// Check if the last email/phone number field is filled (i.e.: not empty),
	// and use above form watchers/counts to help determine enabling/disabling "Add" buttons.
	const canAddEmail = () => {
		if (emailsCount > 0 && emailsWatch[emailsCount - 1].email === '') {
			return false;
		}
		// Need room for the separator + at least a minimal email address.
		const emailsUsedLength = emailsWatch
			.map((e) => e.email)
			.filter((e) => e !== '')
			.join(BE_EMAILS_LIST_SEPARATOR).length;
		const requiredRoom =
			(emailsUsedLength > 0 ? BE_EMAILS_LIST_SEPARATOR.length : 0) +
			EMAIL_MIN_LENGTH;
		return emailsUsedLength + requiredRoom <= MAX_JOINED_LENGTH.emails;
	};

	const canAddPrimaryPhone = useCallback(() => {
		if (phonesPrimaryCount >= maxCount.phonesPrimary) {
			return false;
		}
		if (phonesPrimaryCount === 0) {
			return true;
		}
		return phonesPrimaryWatch[phonesPrimaryCount - 1].phone !== '';
	}, [phonesPrimaryWatch, phonesPrimaryCount]);

	const canAddSecondaryPhone = useCallback(() => {
		if (phonesSecondaryCount >= maxCount.phonesSecondary) {
			return false;
		}
		if (phonesSecondaryCount === 0) {
			return true;
		}
		return phonesSecondaryWatch[phonesSecondaryCount - 1].phone !== '';
	}, [phonesSecondaryWatch, phonesSecondaryCount]);

	const countryOptions = useCountryOptions();

	return (
		<>
			<Helmet
				title={t(
					'pages.account.organisation.profile.title',
					'Organisation - Profile',
				).toString()}
			/>
			<div className="space-y-6">
				<div>
					<h3 className="text-lg font-medium">
						{t('pages.account.organisation.profile.title', 'General')}
					</h3>
					<p className="text-muted-foreground text-sm">
						{t(
							'pages.account.organisation.profile.description',
							'This is how others will see you on the site.',
						)}
					</p>
				</div>
				<Separator />
				<div className="sm:col-span-full">
					<h5 className="text-secondary-900 mb-2 flex items-center text-sm font-medium leading-6">
						{t('pages.organisation.settings.general.rating.label', 'Rating')}
					</h5>
					<div className="flex flex-row items-center justify-start">
						<Stars rating={Math.round(organization.rating.averageRating)} />
						<span className="ml-2 mt-1 text-xs">
							{`(${t('general.reviews', {
								count: organization.rating.totalRatings,
							})})`}
						</span>
					</div>
				</div>
				<Form {...form}>
					<form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
						<div className="grid grid-flow-col gap-3">
							<FormItem>
								<FormLabel>
									{t(
										'pages.organisation.settings.general.org_number.label',
										'Organisation number',
									)}
								</FormLabel>
								<FormControl>
									<FormFieldReadOnly value={organization.id.toString()} />
								</FormControl>
							</FormItem>

							<FormItem>
								<FormLabel>
									{t(
										'pages.organisation.settings.general.org_type.label',
										'Organisation type',
									)}
								</FormLabel>
								<FormControl>
									<FormFieldReadOnly value={orgType} />
								</FormControl>
								<FormMessage />
							</FormItem>
						</div>

						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											canUpdate && requiredFields.name ? 'required-asterix' : ''
										}
									>
										{t('general.name', 'Organisation name')}
									</FormLabel>
									<FormControl>
										<Input {...field} disabled={!canUpdate} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="flex flex-col space-y-2">
							<FormLabel
								className={
									canUpdate && requiredFields.emails ? 'required-asterix' : ''
								}
							>
								{t(
									'pages.organisation.settings.general.invoice_email.label',
									'Invoice email(s)',
								)}
							</FormLabel>
							{fields.map((field, index) => (
								<FormField
									key={field.id}
									control={form.control}
									name={`emails.${index}.email`}
									render={({ field: subField }) => (
										<FormItem>
											<div className="flex gap-2">
												<FormControl>
													<Input {...subField} disabled={!canUpdate} />
												</FormControl>
												{canUpdate && index !== 0 && (
													<Button
														type="button"
														variant="destructive"
														size="icon"
														onClick={() => remove(index)}
													>
														<Icon icon={faTrashCan} />
													</Button>
												)}
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>
							))}
							{canUpdate && canAddEmail() && (
								<div>
									<Button
										type="button"
										className="space-x-2 p-0"
										variant="link"
										onClick={() => append({ email: '' })}
									>
										<Icon icon={faPlus} />
										<span>
											{t(
												'pages.organisation.settings.general.invoice_email.add',
												'Add email',
											)}
										</span>
									</Button>
								</div>
							)}
						</div>

						<FormField
							control={form.control}
							name="website"
							render={({ field }) => (
								<FormItem>
									<FormLabel
										className={
											canUpdate && requiredFields.website
												? 'required-asterix'
												: ''
										}
									>
										{t(
											'pages.organisation.settings.general.website.label',
											'Website',
										)}
									</FormLabel>
									<FormControl>
										<Input {...field} disabled={!canUpdate} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="flex items-start justify-between gap-3">
							<div className="flex-1 space-y-2">
								<FormLabel className="flex items-center gap-2">
									<span
										className={
											canUpdate && requiredFields.phoneNumbers
												? 'required-asterix'
												: ''
										}
									>
										{t(
											'pages.organisation.settings.general.phone.label',
											'Phone',
										)}
									</span>
								</FormLabel>
								{phonesPrimaryFieldArray.fields.map((field, index) => (
									<FormField
										key={field.id}
										control={form.control}
										name={`phonesPrimary.${index}.phone`}
										render={({ field }) => (
											<FormItem>
												<div className="flex gap-2">
													<FormControl>
														<Input
															{...field}
															value={phoneNumber.getValue(field.value)}
															placeholder={t(
																'general.phone_example',
																'+31612345678',
															).toString()}
															disabled={!canUpdate}
														/>
													</FormControl>
													{canUpdate && index !== 0 && (
														<Button
															type="button"
															variant="destructive"
															size="icon"
															onClick={() =>
																phonesPrimaryFieldArray.remove(index)
															}
														>
															<Icon icon={faTrashCan} />
														</Button>
													)}
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								))}
								{canUpdate && canAddPrimaryPhone() && (
									<div>
										<Button
											type="button"
											className="space-x-2 p-0"
											variant="link"
											onClick={() =>
												phonesPrimaryFieldArray.append({ phone: '' })
											}
										>
											<Icon icon={faPlus} />
											<span>
												{t(
													'pages.organisation.settings.general.phone.add',
													'Add phone number',
												)}
											</span>
										</Button>
									</div>
								)}
							</div>
							<div className="flex-1 space-y-2">
								<FormLabel className="flex items-center gap-2">
									<span
										className={
											canUpdate && requiredFields.phoneNumbers
												? 'required-asterix'
												: ''
										}
									>
										{t(
											'pages.organisation.settings.general.out_of_office_number.label',
											'Out of office number',
										)}
									</span>
									<TooltipProvider delayDuration={10}>
										<Tooltip>
											<TooltipTrigger type="button">
												<Icon icon={faInfoCircle} className="size-4" />
											</TooltipTrigger>
											<TooltipContent className="max-w-xs text-xs">
												{t(
													'pages.organisation.settings.general.out_of_office_number.tooltip',
													'Add details in brackets, like the contact person or preferred calling hours (e.g. +31612345678 (call between 9–17))',
												)}
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</FormLabel>
								{phonesSecondaryFieldArray.fields.map((field, index) => (
									<FormField
										key={field.id}
										control={form.control}
										name={`phonesSecondary.${index}.phone`}
										render={({ field }) => (
											<FormItem>
												<div className="flex gap-2">
													<FormControl>
														<Input
															{...field}
															value={phoneNumber.getValue(field.value)}
															placeholder={t(
																'general.phone_example',
																'+31612345678',
															).toString()}
															disabled={!canUpdate}
														/>
													</FormControl>
													{canUpdate && index !== 0 && (
														<Button
															type="button"
															variant="destructive"
															size="icon"
															onClick={() =>
																phonesSecondaryFieldArray.remove(index)
															}
														>
															<Icon icon={faTrashCan} />
														</Button>
													)}
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								))}
								{canUpdate && canAddSecondaryPhone() && (
									<div>
										<Button
											type="button"
											className="space-x-2 p-0"
											variant="link"
											onClick={() =>
												phonesSecondaryFieldArray.append({
													phone: '',
												})
											}
										>
											<Icon icon={faPlus} />
											<span>
												{t(
													'pages.organisation.settings.general.phone.add',
													'Add phone number',
												)}
											</span>
										</Button>
									</div>
								)}
							</div>
						</div>

						<div className="space-y-3">
							<div className="grid grid-cols-[3fr,_1fr] gap-3">
								<FormField
									control={form.control}
									name="address.street"
									render={({ field }) => (
										<FormItem>
											<FormLabel
												className={
													canUpdate && requiredFields['address.street']
														? 'required-asterix'
														: ''
												}
											>
												{t('general.address.street', 'Street')}
											</FormLabel>
											<FormControl>
												<Input {...field} disabled={!canUpdate} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="address.houseNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel
												className={
													canUpdate && requiredFields['address.houseNumber']
														? 'required-asterix'
														: ''
												}
											>
												{t('general.address.house_number', 'House number')}
											</FormLabel>
											<FormControl>
												<Input {...field} disabled={!canUpdate} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="grid grid-cols-[2fr,_4fr] gap-3 ">
								<FormField
									control={form.control}
									name="address.postalCode"
									render={({ field }) => (
										<FormItem>
											<FormLabel
												className={
													canUpdate && requiredFields['address.postalCode']
														? 'required-asterix'
														: ''
												}
											>
												{t('general.address.postalCode', 'Postal code')}
											</FormLabel>
											<FormControl>
												<Input {...field} disabled={!canUpdate} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="address.city"
									render={({ field }) => (
										<FormItem>
											<FormLabel
												className={
													canUpdate && requiredFields['address.city']
														? 'required-asterix'
														: ''
												}
											>
												{t('general.address.city', 'City')}
											</FormLabel>
											<FormControl>
												<Input {...field} disabled={!canUpdate} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="address.countryCode"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel
											className={
												canUpdate && requiredFields['address.countryCode']
													? 'required-asterix'
													: ''
											}
										>
											{t('general.address.country', 'Country')}
										</FormLabel>
										<FormControl>
											<Combobox
												ref={field.ref}
												defaultValue={field.value}
												onChange={field.onChange}
												options={countryOptions.map((country) => ({
													value: country.code,
													label: country.name,
													onSelect: () => {
														form.setValue(
															'address.countryCode',
															country.code,
															shouldUpdate,
														);
													},
												}))}
												className="max-h-[300px] bg-transparent"
												placeholder={t(
													'pages.account.organisation.profile.form.country.dropdown.placeholder',
													'Select country',
												).toString()}
												searchPlaceholder={t(
													'pages.account.organisation.profile.form.country.dropdown.search-placeholder',
													'Search country...',
												).toString()}
												emptyMessage={t(
													'pages.account.organisation.profile.form.country.dropdown.no-result',
													'No country found.',
												).toString()}
												disabled={!canUpdate}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{canUpdate && (
							<div className="flex flex-row justify-end">
								<Button
									type="submit"
									disabled={isUpdating || !form.formState.isValid}
								>
									{isUpdating && (
										<Icon spin className="mr-3" icon={faSpinnerThird} />
									)}
									{t('general.actions.save', 'save')}
								</Button>
							</div>
						)}
					</form>
					{import.meta.env.DEV && (
						<DevTool control={form.control} placement="top-right" />
					)}
				</Form>
			</div>
		</>
	);
}
